class MarkerBuildPack {
  constructor(section) {
    this.section = section;
    this.moneyFormat = section.dataset.moneyFormat || '${{amount}}';
    this.tiers = [...section.querySelectorAll('[data-pack-tier]')];
    this.products = [...section.querySelectorAll('[data-pack-product]')];
    this.slots = section.querySelector('[data-pack-slots]');
    this.hint = section.querySelector('[data-pack-hint]');
    this.saving = section.querySelector('[data-pack-saving]');
    this.submit = section.querySelector('[data-pack-submit]');
    this.submitLabel = section.querySelector('[data-pack-submit-label]');
    this.error = section.querySelector('[data-pack-error]');
    this.picks = [];
    this.activeTier = this.tiers[0] || null;

    if (!this.activeTier) return;
    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.tiers.forEach((tier) => tier.addEventListener('click', () => {
      this.activeTier = tier;
      this.tiers.forEach((item) => item.classList.toggle('is-active', item === tier));
      this.picks = this.picks.slice(0, this.packSize());
      this.render();
    }));

    this.products.forEach((product) => product.addEventListener('click', () => {
      if (product.dataset.available !== 'true') return;
      const id = product.dataset.variantId;
      if (this.picks.length >= this.packSize()) {
        const existingIndex = this.picks.indexOf(id);
        if (existingIndex >= 0) this.picks.splice(existingIndex, 1);
        else this.flashFull();
      } else {
        this.picks.push(id);
      }
      this.render();
    }));

    this.slots?.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-slot]');
      if (!remove) return;
      this.picks.splice(Number(remove.dataset.removeSlot), 1);
      this.render();
    });

    this.submit?.addEventListener('click', () => this.addPack());
  }

  packSize() { return Number(this.activeTier?.dataset.size || 0); }
  packDiscount() { return Number(this.activeTier?.dataset.discount || 0); }
  packLabel() { return this.activeTier?.dataset.label || `${this.packSize()}-Pack`; }

  regularTotal() {
    return this.picks.reduce((total, id) => {
      const product = this.products.find((item) => item.dataset.variantId === id);
      return total + Number(product?.dataset.price || 0);
    }, 0);
  }

  discountedTotal() {
    return Math.round(this.regularTotal() * (100 - this.packDiscount()) / 100);
  }

  render() {
    this.renderProducts();
    this.renderSlots();
    this.renderSummary();
  }

  renderProducts() {
    this.products.forEach((product) => {
      const quantity = this.picks.filter((id) => id === product.dataset.variantId).length;
      product.classList.toggle('is-picked', quantity > 0);
      const badge = product.querySelector('[data-picked-quantity]');
      if (badge) {
        badge.hidden = quantity === 0;
        badge.textContent = quantity;
      }
      const action = product.querySelector('[data-product-action]');
      if (action && product.dataset.available === 'true') {
        action.textContent = quantity ? this.section.dataset.addedLabel : this.section.dataset.addLabel;
      }
    });
  }

  renderSlots() {
    if (!this.slots) return;
    this.slots.innerHTML = Array.from({ length: this.packSize() }, (_, index) => {
      const id = this.picks[index];
      if (!id) return `<div class="marker-pack__slot"><span>${index + 1}</span></div>`;
      const product = this.products.find((item) => item.dataset.variantId === id);
      if (!product) return `<div class="marker-pack__slot"><span>${index + 1}</span></div>`;
      return `<div class="marker-pack__slot is-filled"><img src="${this.escape(product.dataset.productImage)}" alt="${this.escape(product.dataset.productTitle)}"><button type="button" class="marker-pack__remove" data-remove-slot="${index}" aria-label="Remove ${this.escape(product.dataset.productTitle)}">&times;</button></div>`;
    }).join('');
  }

  renderSummary() {
    const remaining = this.packSize() - this.picks.length;
    if (this.hint) {
      this.hint.innerHTML = remaining
        ? `Pick <b>${remaining}</b> more ${remaining === 1 ? 'marker' : 'markers'} to complete your ${this.packLabel().toLowerCase()}`
        : `<b>Pack complete!</b> ${this.packLabel()} · ${this.formatMoney(this.discountedTotal())}`;
    }

    const saving = Math.max(0, this.regularTotal() - this.discountedTotal());
    if (this.saving) this.saving.textContent = !remaining && saving > 0 ? `${this.section.dataset.savingLabel} ${this.formatMoney(saving)}` : '';

    if (this.submit) this.submit.disabled = remaining > 0;
    if (this.submitLabel) {
      this.submitLabel.textContent = remaining
        ? `${this.section.dataset.incompleteLabel.replace(/\s+your\s+markers?$/i, '').replace(/\s+markers?$/i, '')} ${remaining} more`
        : `${this.section.dataset.completeLabel} ${this.packLabel()} · ${this.formatMoney(this.discountedTotal())}`;
    }
  }

  flashFull() {
    this.hint?.classList.add('is-full');
    window.setTimeout(() => this.hint?.classList.remove('is-full'), 600);
  }

  async addPack() {
    if (!this.submit || this.picks.length !== this.packSize()) return;
    this.setLoading(true);
    this.showError('');

    const quantities = this.picks.reduce((items, id) => {
      items[id] = (items[id] || 0) + 1;
      return items;
    }, {});
    const items = Object.entries(quantities).map(([id, quantity]) => ({
      id: Number(id),
      quantity,
      properties: {
        '_Marker pack': this.packLabel(),
        '_Marker pack discount': `${this.packDiscount()}%`,
        '_Expected pack total': this.formatMoney(this.discountedTotal()),
      },
    }));

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items };
      if (cartDrawer?.getSectionsToRender) {
        body.sections = cartDrawer.getSectionsToRender().map((item) => item.id);
        body.sections_url = window.location.pathname;
        cartDrawer.setActiveElement?.(document.activeElement);
      }
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (error) {
        throw new Error('Shopify returned an invalid cart response. Please refresh and try again.');
      }
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this pack.');

      if (cartDrawer && result.sections && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(result);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      this.showError(error.message || 'Unable to add this pack. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    this.submit?.classList.toggle('loading', loading);
    if (this.submit) this.submit.disabled = loading || this.picks.length !== this.packSize();
    this.submit?.setAttribute('aria-disabled', loading ? 'true' : 'false');
    this.submit?.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  showError(message) {
    if (!this.error) return;
    this.error.hidden = !message;
    this.error.textContent = message;
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.moneyFormat);
    const value = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  escape(value = '') {
    const element = document.createElement('div');
    element.textContent = value;
    return element.innerHTML;
  }
}

const initMarkerBuildPacks = (scope = document) => {
  scope.querySelectorAll('[data-marker-pack]').forEach((section) => {
    if (!section.markerBuildPack) section.markerBuildPack = new MarkerBuildPack(section);
  });
};

document.addEventListener('DOMContentLoaded', () => initMarkerBuildPacks());
document.addEventListener('shopify:section:load', (event) => initMarkerBuildPacks(event.target));
